import { Button } from '@ctw/shared/components/button';
import { Card } from '@ctw/shared/components/containers/card';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { Link } from '@ctw/shared/components/link';
import type { PatientSummaryJobStatus } from '@ctw/shared/content/summarization/use-patient-summaries';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useInterval } from '@ctw/shared/hooks/use-interval';
import { formatDateToLocalHumanDateString, numberOfDaysBetween } from '@ctw/shared/utils/dates';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faMessageDots } from '@fortawesome/pro-duotone-svg-icons';
import {
  faArrowTrendUp,
  faArrowUpRightFromSquare,
  faCircleCheck,
  faCircleExclamation,
  faEnvelope,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays, endOfMonth } from 'date-fns';
import pluralize from 'pluralize';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface PatientSummarizationJobResult {
  status: PatientSummaryJobStatus;
}

export const UI_FLAG_PATIENT_SUMMARIZATION_LIMIT_APPROACHING_BANNER_HIDDEN =
  'patient-summarization-limit-approaching-banner-hidden' as const;
export const UI_FLAG_PATIENT_SUMMARIZATION_LIMIT_REACHED_BANNER_HIDDEN =
  'patient-summarization-limit-reached-banner-hidden' as const;

export type PatientSummarizationCardState =
  | 'loading'
  | 'generating'
  | 'success'
  | 'limit_not_reached'
  | 'limit_approaching'
  | 'limit_reached';

interface DeterminePatientSummarizationCardStateArgs {
  userPatientSummarizationState: ReturnType<typeof useUserPatientSummarizationContext>;
  patientSummarizationResult: PatientSummarizationJobResult | null;
  isCheckingCache?: boolean;
}

const determinePatientSummarizationCardState = ({
  userPatientSummarizationState,
  patientSummarizationResult,
  isCheckingCache,
}: DeterminePatientSummarizationCardStateArgs): PatientSummarizationCardState => {
  if (isCheckingCache) {
    return 'loading';
  }

  const { summarizationLimitReached, summarizationLimitApproaching } =
    userPatientSummarizationState;
  const summarizationJobExists =
    patientSummarizationResult !== null && patientSummarizationResult.status !== 'error';
  const summarizationJobCompleted = patientSummarizationResult?.status === 'completed';
  const summarizationJobRunning = patientSummarizationResult?.status === 'running';

  if (summarizationJobRunning) {
    return 'generating';
  }

  if (summarizationJobCompleted) {
    return 'success';
  }

  if (!(summarizationJobExists || summarizationLimitReached || summarizationLimitApproaching)) {
    return 'limit_not_reached';
  }

  if (!summarizationJobExists && summarizationLimitApproaching) {
    return 'limit_approaching';
  }

  return 'limit_reached';
};

export interface PatientSummarizationStatusCardProps {
  patientSummarizationResult: PatientSummarizationJobResult | null;
  onGenerateSummary: () => void;
  isCheckingCache?: boolean;
}

export const PatientSummarizationStatusCard = ({
  patientSummarizationResult,
  onGenerateSummary,
  isCheckingCache,
}: PatientSummarizationStatusCardProps) => {
  const userPatientSummarizationState = useUserPatientSummarizationContext();

  const patientSummarizationCardState = determinePatientSummarizationCardState({
    userPatientSummarizationState,
    patientSummarizationResult,
    isCheckingCache,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [animatedHeight, setAnimatedHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setAnimatedHeight(el.scrollHeight);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Card
      testId="patient-summarization-status-card"
      fullbleed={true}
      className={twx('bg-background-main', {
        'border-2 border-caution-main/15': patientSummarizationCardState === 'limit_approaching',
        'border-2 border-error-main/15': patientSummarizationCardState === 'limit_reached',
      })}
    >
      <div
        className={tw`overflow-hidden transition-[height] duration-300 ease-out`}
        style={{ height: animatedHeight !== undefined ? `${animatedHeight}px` : 'auto' }}
      >
        <div ref={contentRef}>
          {patientSummarizationCardState === 'loading' && <PatientSummarizationLoading />}
          {patientSummarizationCardState === 'generating' && (
            <PatientSummarizationGeneratingSummary />
          )}
          {patientSummarizationCardState === 'success' && <PatientSummarizationSuccess />}
          {patientSummarizationCardState === 'limit_not_reached' && (
            <PatientSummarizationLimitNotReached generateSummary={onGenerateSummary} />
          )}
          {patientSummarizationCardState === 'limit_approaching' && (
            <PatientSummarizationLimitApproaching generateSummary={onGenerateSummary} />
          )}
          {patientSummarizationCardState === 'limit_reached' && (
            <PatientSummarizationLimitReached />
          )}
        </div>
      </div>
    </Card>
  );
};

const PatientSummarizationLoading = () => {
  return (
    <div className={tw`relative`}>
      {/* Invisible instance of the default card state to reserve the correct height */}
      <div className={tw`invisible`} aria-hidden="true">
        <PatientSummarizationLimitNotReached
          generateSummary={() => {
            /* noop */
          }}
        />
      </div>
      <div className={tw`absolute inset-0 flex items-center gap-3 p-4`}>
        <FontAwesomeIcon
          icon={faSparkles}
          className={tw`h-5 w-5 pt-0.5 text-primary-main`}
          beatFade={true}
          style={{ '--fa-beat-fade-opacity': '0.67', '--fa-beat-fade-scale': '1.075' }}
        />
        <div className={tw`text-content-subtle text-sm`}>Checking for patient summary...</div>
      </div>
    </div>
  );
};

const LOADING_BAR_TICK_INTERVAL_MS = 150 as const;
const LOADING_BAR_WIDTH_STEP = 0.5 as const;

const PatientSummarizationGeneratingSummary = () => {
  const { summarizationLimitApproaching } = useUserPatientSummarizationContext();
  const [loadingBarTickIntervalMilliseconds, setLoadingBarTickIntervalMilliseconds] = useState<
    number | null
  >(LOADING_BAR_TICK_INTERVAL_MS);
  const [loadingBarWidth, setLoadingBarWidth] = useState(0);

  useInterval({
    intervalInMilliseconds: loadingBarTickIntervalMilliseconds,
    intervalFunction: () => {
      setLoadingBarWidth((previousWidth) => {
        const progressedWidth = Math.min(previousWidth + LOADING_BAR_WIDTH_STEP, 100);

        if (progressedWidth >= 100) {
          setLoadingBarTickIntervalMilliseconds(null);
        }

        return progressedWidth;
      });
    },
  });

  return (
    <div>
      <div className={tw`flex flex-col justify-between gap-3 p-4 md:flex-row`}>
        <div className={tw`flex items-center justify-start gap-3`}>
          <FontAwesomeIcon
            icon={faSparkles}
            className={tw`h-5 w-5 pt-0.5 text-primary-main`}
            beatFade={true}
            style={{ '--fa-beat-fade-opacity': '0.67', '--fa-beat-fade-scale': '1.075' }}
          />
          <div>
            <div className={tw`text-content-main`}>Generating patient summary...</div>
            <div className={tw`text-content-subtle text-sm`}>Loading patient chart entries...</div>
          </div>
        </div>
        <div className={tw`flex items-center justify-start gap-3`}>
          <Button
            type="button"
            variant="primary"
            className={tw`cursor-wait hover:bg-primary-main active:scale-100`}
          >
            <div className={tw`flex items-center space-x-2`}>
              <div className={tw`whitespace-nowrap text-sm`}>Generating summary</div>
              <FontAwesomeIcon
                icon={faMessageDots}
                beatFade={true}
                style={{ '--fa-beat-fade-opacity': '0.67', '--fa-beat-fade-scale': '1.075' }}
                className={tw`h-4 w-4`}
              />
            </div>
          </Button>
        </div>
      </div>
      <div
        className={tw`h-1 animate-gentle-pulse bg-logo-green/85 transition-[width] ease-linear`}
        style={{
          width: `${loadingBarWidth}%`,
          transitionDuration: `${LOADING_BAR_TICK_INTERVAL_MS}ms`,
        }}
      />
      {summarizationLimitApproaching && <PatientSummarizationLimitApproachingBanner />}
    </div>
  );
};

const PatientSummarizationSuccess = () => {
  const { navigate } = useZuiContext();

  return (
    <div>
      <div className={tw`flex flex-col justify-between gap-3 p-4 md:flex-row`}>
        <div className={tw`flex items-center justify-start gap-3`}>
          <FontAwesomeIcon icon={faSparkles} className={tw`h-5 w-5 pt-0.5 text-success-main/95`} />
          <div className={tw`text-content-main`}>Patient Summary Ready</div>
        </div>
        <div className={tw`flex items-center justify-start gap-3`}>
          <Button type="button" variant="primary" onClick={() => navigate('summary')}>
            <div className={tw`flex items-center space-x-2`}>
              <FontAwesomeIcon icon={faCircleCheck} className={tw`h-4 w-4`} />
              <div className={tw`whitespace-nowrap text-sm`}>View Patient Summary</div>
            </div>
          </Button>
        </div>
      </div>
      <div className={tw`h-1 w-full bg-success-main/95 transition-all duration-500`} />
    </div>
  );
};

interface PatientSummarizationLimitNotReachedProps {
  generateSummary: () => void;
}

const PatientSummarizationLimitNotReached = ({
  generateSummary,
}: PatientSummarizationLimitNotReachedProps) => {
  return (
    <div className={tw`flex flex-col justify-between gap-3 p-4 md:flex-row`}>
      <div className={tw`flex items-center justify-start gap-3`}>
        <FontAwesomeIcon icon={faSparkles} className={tw`h-5 w-5 pt-0.5 text-primary-main`} />
        <div className={tw`text-content-main`}>Generate a 5-minute briefing for this patient</div>
      </div>
      <div className={tw`flex items-center justify-start gap-3`}>
        <PatientSummarizationUsageTicker />
        <Button type="button" variant="primary" onClick={() => generateSummary()}>
          <div className={tw`flex items-center space-x-2`}>
            <FontAwesomeIcon icon={faSparkles} className={tw`h-4 w-4`} />
            <div className={tw`whitespace-nowrap text-sm`}>Generate Patient Summary</div>
          </div>
        </Button>
      </div>
    </div>
  );
};

interface PatientSummarizationLimitApproachingProps {
  generateSummary: () => void;
}

const PatientSummarizationLimitApproaching = ({
  generateSummary,
}: PatientSummarizationLimitApproachingProps) => {
  return (
    <div>
      <div className={tw`flex flex-col justify-between gap-3 p-4 md:flex-row`}>
        <div className={tw`flex items-center justify-start gap-3`}>
          <FontAwesomeIcon icon={faSparkles} className={tw`h-5 w-5 pt-0.5 text-primary-main`} />
          <div className={tw`text-content-main`}>Generate a 5-minute briefing for this patient</div>
        </div>
        <div className={tw`flex items-center justify-start gap-3`}>
          <PatientSummarizationUsageTicker />
          <Button type="button" variant="primary" onClick={() => generateSummary()}>
            <div className={tw`flex items-center space-x-2`}>
              <FontAwesomeIcon icon={faSparkles} className={tw`h-4 w-4`} />
              <div className={tw`whitespace-nowrap text-sm`}>Generate Patient Summary</div>
            </div>
          </Button>
        </div>
      </div>
      <PatientSummarizationLimitApproachingBanner />
    </div>
  );
};

const PatientSummarizationLimitApproachingBanner = () => {
  const { getUiFlag, setUiFlag } = useZuiContext();
  const { summariesAllotted, summariesUsed } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const hidden = getUiFlag(UI_FLAG_PATIENT_SUMMARIZATION_LIMIT_APPROACHING_BANNER_HIDDEN, false);

  const hideBanner = useCallback(() => {
    setUiFlag(UI_FLAG_PATIENT_SUMMARIZATION_LIMIT_APPROACHING_BANNER_HIDDEN, true);
  }, [setUiFlag]);

  const numberOfDaysUntilMonthlySummaryLimitResets = numberOfDaysBetween({
    start: new Date(),
    end: addDays(endOfMonth(new Date()), 1),
  });

  return (
    <div
      className={tw`grid transition-[grid-template-rows,opacity] duration-300 ease-out`}
      style={{ gridTemplateRows: hidden ? '0fr' : '1fr', opacity: hidden ? 0 : 1 }}
    >
      <div className={tw`min-h-0 overflow-hidden`}>
        <div
          className={tw`flex items-start justify-between gap-4 border-caution-main/15 border-t-2 bg-caution-background p-4 text-caution-text`}
        >
          <div className={tw`flex items-start gap-2`}>
            <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-4 w-4 pt-1`} />
            <div>
              <b>You're approaching your monthly AI summary limit.</b>{' '}
              {summariesUsed.toLocaleString('en-US')} of {summariesAllotted.toLocaleString('en-US')}{' '}
              free summaries have been used this month. Resets in{' '}
              {pluralize('day', numberOfDaysUntilMonthlySummaryLimitResets, true)}.
            </div>
          </div>
          <div className={tw`flex items-center gap-2`}>
            <Button
              type="button"
              variant="link"
              onClick={openUpgradeModal}
              className={tw`whitespace-nowrap font-medium text-warning-text underline`}
            >
              Learn More
            </Button>
            <Button
              type="button"
              variant="circular"
              size="xs"
              className={tw`mt-0.5 flex items-center justify-center bg-transparent p-1 text-warning-text shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
              onClick={hideBanner}
            >
              <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PatientSummarizationLimitReached = () => {
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();

  return (
    <div>
      <div className={tw`flex flex-col justify-between gap-3 p-4 md:flex-row`}>
        <div className={tw`flex items-center justify-start gap-3`}>
          <FontAwesomeIcon icon={faSparkles} className={tw`h-5 w-5 text-error-text`} />
          <div className={tw`text-error-text`}>You've reached your monthly AI summary limit</div>
        </div>
        <div className={tw`flex items-center justify-start gap-3`}>
          <PatientSummarizationUsageTicker />
          <Button type="button" variant="primary-destructive" onClick={openUpgradeModal}>
            <div className={tw`flex items-center space-x-2`}>
              <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
              <div className={tw`whitespace-nowrap text-sm`}>Upgrade Now</div>
            </div>
          </Button>
        </div>
      </div>
      <PatientSummarizationLimitReachedBanner />
    </div>
  );
};

const PatientSummarizationLimitReachedBanner = () => {
  const { getUiFlag, setUiFlag } = useZuiContext();
  const { summariesAllotted } = useUserPatientSummarizationContext();
  const hidden = getUiFlag(UI_FLAG_PATIENT_SUMMARIZATION_LIMIT_REACHED_BANNER_HIDDEN, false);

  const hideBanner = useCallback(() => {
    setUiFlag(UI_FLAG_PATIENT_SUMMARIZATION_LIMIT_REACHED_BANNER_HIDDEN, true);
  }, [setUiFlag]);
  const formattedSummaryLimitResetDate = formatDateToLocalHumanDateString(
    addDays(endOfMonth(new Date()), 1),
  );

  return (
    <div
      className={tw`grid transition-[grid-template-rows,opacity] duration-300 ease-out`}
      style={{ gridTemplateRows: hidden ? '0fr' : '1fr', opacity: hidden ? 0 : 1 }}
    >
      <div className={tw`min-h-0 overflow-hidden`}>
        <div
          className={tw`flex items-start justify-between gap-2 border-error-main/15 border-t-2 bg-error-background p-4 text-error-text`}
        >
          <div className={tw`flex items-start gap-2`}>
            <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-4 w-4 pt-1`} />
            <div>
              You've used all {summariesAllotted.toLocaleString('en-US')} free summaries this month.
              Upgrade for unlimited access or wait until your quota resets (
              {formattedSummaryLimitResetDate}).
            </div>
          </div>
          <Button
            type="button"
            variant="circular"
            size="xs"
            className={tw`flex items-center justify-center bg-transparent p-1 text-warning-text shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
            onClick={hideBanner}
          >
            <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
          </Button>
        </div>
      </div>
    </div>
  );
};

const UPGRADE_BENEFITS = [
  {
    title: 'Summaries for every patient',
    description: 'Generate clinical summaries for your entire panel without monthly caps.',
  },
  {
    title: 'Time-saving automation',
    description: 'Save hours each week by automating the synthesis of complex patient history.',
  },
  {
    title: 'Priority feature access',
    description: 'Be the first to use new beta summary features.',
  },
] as const;

interface PatientSummarizationUpgradeModalBodyProps {
  closeModal: () => void;
}

const PatientSummarizationUpgradeModalBody = ({
  closeModal,
}: PatientSummarizationUpgradeModalBodyProps) => {
  const { summariesAllotted } = useUserPatientSummarizationContext();

  const [shouldCcAdminOrManager, setShouldCcAdminOrManager] = useState(true);
  const [adminOrManagerEmail, setAdminOrManagerEmail] = useState<string | null>(null);
  const [additionalNotes, setAdditionalNotes] = useState<string | null>(null);

  const handleSubmit = () => {
    // TODO: wire up upgrade request submission
    closeModal();
  };

  return (
    <div className={tw`relative flex w-[480px] max-w-full flex-col`}>
      <div
        className={tw`flex items-start gap-3 border-success-badge border-b bg-success-badge/55 p-5`}
      >
        <div
          className={tw`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white drop-shadow`}
        >
          <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-5 w-5 text-primary-main`} />
        </div>
        <div>
          <h2 className={tw`font-bold font-semibold text-lg text-success-text`}>
            Unlock Full AI Access
          </h2>
          <p className={tw`text-sm text-success-text`}>
            You've used all {summariesAllotted.toLocaleString('en-US')} free summaries this month.
          </p>
        </div>
      </div>

      <div className={tw`px-6 pt-6`}>
        <div className={tw`font-semibold text-content-main text-sm uppercase tracking-wider`}>
          When you upgrade, you get:
        </div>
        <ul className={tw`mt-3 flex flex-col gap-3`}>
          {UPGRADE_BENEFITS.map((benefit) => (
            <li key={benefit.title} className={tw`flex items-start gap-2.5`}>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className={tw`mt-1 h-4 w-4 shrink-0 text-primary-main`}
              />
              <div>
                <div className={tw`font-semibold text-content-main`}>{benefit.title}</div>
                <div className={tw`text-content-subtle text-sm`}>{benefit.description}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={tw`mx-6 mt-6 flex flex-col gap-3 rounded-lg border border-border-main/50 bg-content-focal/40 p-4`}
      >
        <div>
          <Checkbox
            checked={shouldCcAdminOrManager}
            onChange={setShouldCcAdminOrManager}
            name="cc-admin-manager"
            label="CC my team admin or manager on this request"
          />
          <p className={tw`w-full pl-7 text-content-subtle text-xs leading-tight`}>
            We'll include them in the email to expedite the approval process.
          </p>
        </div>
        {shouldCcAdminOrManager && (
          <div className={tw`flex items-center justify-center gap-3 pt-1`}>
            <div className={tw`w-full`}>
              <label
                htmlFor="upgrade-admin-or-manager-email"
                className={tw`flex items-start font-semibold text-content-subtle text-xs uppercase tracking-wider`}
              >
                <div className={tw`leading-tight`}>Admin or Manager email</div>
                <div className={tw`-mt-0.5 text-error-text text-xl leading-none`}>*</div>
              </label>
              <input
                id="upgrade-admin-or-manager-email"
                type="email"
                required={true}
                inputMode="email"
                autoComplete="email"
                value={adminOrManagerEmail ?? ''}
                onChange={(event) => {
                  setAdminOrManagerEmail(
                    `${event.target.value}`.trim().length === 0 ? null : event.target.value,
                  );
                }}
                placeholder="manager@company.com"
                className={tw`w-full rounded-md border border-border-main px-3 py-2 text-content-main text-sm placeholder:text-content-subtle/50 focus:border-primary-main focus:outline-none focus:ring-1 focus:ring-primary-main`}
              />
            </div>
          </div>
        )}
      </div>
      <div className={tw`px-6 pt-4`}>
        <label
          htmlFor="upgrade-additional-notes"
          className={tw`block font-semibold text-content-subtle text-xs uppercase tracking-wider`}
        >
          Additional notes{' '}
          <span className={tw`font-normal normal-case tracking-normal`}>(optional)</span>
        </label>
        <textarea
          id="upgrade-additional-notes"
          value={additionalNotes ?? ''}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Any specific requirements or questions..."
          rows={3}
          className={tw`mt-1.5 w-full resize-none rounded-md border border-border-main px-3 py-2 text-content-main text-sm placeholder:text-content-subtle/50 focus:border-primary-main focus:outline-none focus:ring-1 focus:ring-primary-main`}
        />
      </div>

      <div className={tw`flex flex-col items-center gap-3 px-6 pt-4 pb-6`}>
        <Button
          type="button"
          variant="primary"
          className={tw`w-full justify-center`}
          onClick={handleSubmit}
        >
          <div className={tw`flex items-center justify-center gap-2`}>
            <FontAwesomeIcon icon={faEnvelope} className={tw`h-4 w-4`} />
            <div className={tw`font-bold text-[0.9rem]`}>Request Upgrade</div>
          </div>
        </Button>
        <div className={tw`flex flex-col items-center gap-1`}>
          <p className={tw`text-content-subtle text-xs`}>
            Our team will reach out within 1-2 business days to process your request.
          </p>
          <Link
            target="_blank"
            href="https://clinicalguide.zushealth.com/docs/pilot-ai-summarization-the-generated-patient-summary"
            className={tw`inline-flex items-center gap-2`}
          >
            <div className={tw`font-semibold text-sm`}>Learn more about AI Summary pricing</div>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={tw`h-3 w-3`} />
          </Link>
        </div>
      </div>
    </div>
  );
};

const usePatientSummarizationUpgradeModal = () => {
  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'PatientSummarizationUpgradeModal',
    layout: {
      mode: 'fit',
      overflowBehavior: 'visible',
      fullbleed: true,
      showCloseButton: true,
    },
  });

  const openUpgradeModal = useCallback(
    () =>
      openModal({
        telemetry,
        action: {
          label: 'Upgrade',
          title: 'Unlock Full AI Access',
          content: {
            body: <PatientSummarizationUpgradeModalBody closeModal={closeModal} />,
          },
        },
      }),
    [telemetry, openModal, closeModal],
  );

  return { openUpgradeModal, closeUpgradeModal: closeModal };
};

const USAGE_RING_VIEW = 16;
const USAGE_RING_STROKE = 2;
const USAGE_RING_RADIUS = (USAGE_RING_VIEW - USAGE_RING_STROKE) / 2;
const USAGE_RING_CENTER = USAGE_RING_VIEW / 2;
const USAGE_RING_CIRCUMFERENCE = 2 * Math.PI * USAGE_RING_RADIUS;

const PatientSummarizationUsageTicker = () => {
  const {
    summariesAllotted,
    summariesUsed,
    summarizationLimitApproaching,
    summarizationLimitReached,
  } = useUserPatientSummarizationContext();

  const hasFreeUsageLimit = useFeatureFlag('summarization-free-usage-limit');
  if (!hasFreeUsageLimit) {
    // Don't show this meter unless the metering flag is on
    return null;
  }

  const safeAllotted = Math.max(summariesAllotted, 1);
  const fractionUsed = summarizationLimitReached
    ? 1
    : Math.min(1, Math.max(0, summariesUsed / safeAllotted));
  const progressDashOffset = USAGE_RING_CIRCUMFERENCE * (1 - fractionUsed);

  return (
    <div
      className={twx('flex items-center gap-0.5 whitespace-nowrap rounded-md border px-2 py-1', {
        'border-border-main/45 bg-background-disabled/35': !(
          summarizationLimitReached || summarizationLimitApproaching
        ),
        'border-caution-text/30 bg-caution-background':
          !summarizationLimitReached && summarizationLimitApproaching,
        'border-error-text/30 bg-error-background': summarizationLimitReached,
      })}
    >
      <svg
        viewBox={`0 0 ${USAGE_RING_VIEW} ${USAGE_RING_VIEW}`}
        width={USAGE_RING_VIEW}
        height={USAGE_RING_VIEW}
        aria-hidden="true"
        role="presentation"
        className={tw`h-4 w-4 shrink-0`}
      >
        <circle
          cx={USAGE_RING_CENTER}
          cy={USAGE_RING_CENTER}
          r={USAGE_RING_RADIUS}
          fill="none"
          className={twx({
            'stroke-content-subtle/30': !(
              summarizationLimitReached || summarizationLimitApproaching
            ),
            'stroke-content-subtle/50': summarizationLimitReached || summarizationLimitApproaching,
          })}
          strokeWidth={USAGE_RING_STROKE}
        />
        <circle
          cx={USAGE_RING_CENTER}
          cy={USAGE_RING_CENTER}
          r={USAGE_RING_RADIUS}
          fill="none"
          className={twx({
            'stroke-content-subtle/55': !(
              summarizationLimitReached || summarizationLimitApproaching
            ),
            'stroke-caution-main/85': !summarizationLimitReached && summarizationLimitApproaching,
            'stroke-error-main/85': summarizationLimitReached,
          })}
          strokeWidth={USAGE_RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={USAGE_RING_CIRCUMFERENCE}
          strokeDashoffset={progressDashOffset}
          transform={`rotate(-90 ${USAGE_RING_CENTER} ${USAGE_RING_CENTER})`}
        />
      </svg>
      <div
        className={twx('pl-1 font-medium text-sm tracking-wide', {
          'text-content-subtle/75': !(summarizationLimitReached || summarizationLimitApproaching),
          'text-caution-text/85': !summarizationLimitReached && summarizationLimitApproaching,
          'text-error-text/85': summarizationLimitReached,
        })}
      >
        {summariesUsed.toLocaleString('en-US')}&#8239;/&#8239;
        {summariesAllotted.toLocaleString('en-US')}
      </div>
    </div>
  );
};
